
import { WeatherData, Coordinates, DailyWeather, TripWeather, WeatherVerdict, SunPeriod, SunFilterConfig } from "../types";

export const fetchWeather = async (coords: Coordinates): Promise<WeatherData | null> => {
  try {
    const response = await fetch(
      `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${coords.latitude.toFixed(4)}&lon=${coords.longitude.toFixed(4)}`
    );

    if (!response.ok) return null;

    const data = await response.json();
    const current = data.properties.timeseries[0].data.instant.details;
    const next1h = data.properties.timeseries[0].data.next_1_hours;

    return {
      temp: current.air_temperature,
      symbol: next1h?.summary?.symbol_code || 'clearsky_day',
      wind: current.wind_speed,
      rain: next1h?.details?.precipitation_amount || 0
    };
  } catch (error) {
    console.error("Weather fetch error:", error);
    return null;
  }
};

// ─── Reise-copilot: flerdags prognose + vurdering (MET Locationforecast) ───

/** Rå timeseries-entry fra met.no compact (kun feltene vi bruker). */
export interface ForecastEntry {
  time: string;
  data: {
    instant?: { details?: { air_temperature?: number; wind_speed?: number } };
    next_1_hours?: { summary?: { symbol_code?: string }; details?: { precipitation_amount?: number } };
    next_6_hours?: { summary?: { symbol_code?: string }; details?: { precipitation_amount?: number } };
  };
}

const forecastCache = new Map<string, { series: ForecastEntry[]; expiry: number }>();
const FORECAST_TTL_MS = 30 * 60 * 1000; // 30 min

export const coordKey = (coords: Coordinates): string =>
  `${coords.latitude.toFixed(2)},${coords.longitude.toFixed(2)}`;

/** Hent full timeseries for et punkt (cachet per grovt koordinat). [] ved feil. */
export const fetchForecastSeries = async (coords: Coordinates): Promise<ForecastEntry[]> => {
  const key = coordKey(coords);
  const cached = forecastCache.get(key);
  if (cached && cached.expiry > Date.now()) return cached.series;
  try {
    const res = await fetch(
      `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${coords.latitude.toFixed(4)}&lon=${coords.longitude.toFixed(4)}`,
      { signal: AbortSignal.timeout(3500) },
    );
    if (!res.ok) return [];
    const data = await res.json();
    const series: ForecastEntry[] = data?.properties?.timeseries || [];
    forecastCache.set(key, { series, expiry: Date.now() + FORECAST_TTL_MS });
    return series;
  } catch {
    return [];
  }
};

const PRECIP_SYMBOLS = /(rain|sleet|snow|thunder)/i;
/** En dag regnes som «regnvær» ved ≥3 mm nedbør eller nedbør-symbol. */
const RAINY_MM = 3;

/** ISO yyyy-mm-dd fra et met.no-tidsstempel. Ren. */
function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

/**
 * Oppsummer prognosen per dag for vinduet [startDate, startDate+days). Ren funksjon
 * (leser tid fra selve seriene, ikke Date.now → testbar). Nedbør summeres fra 6-timers-
 * blokkene (kl 00/06/12/18) for å unngå dobbelttelling; temp fra instant-verdier.
 */
export function summarizeForecast(series: ForecastEntry[], startDate: string, days: number): TripWeather['dailies'] {
  if (!Array.isArray(series) || series.length === 0) return [];
  const wanted = new Set<string>();
  const start = new Date(`${startDate}T00:00:00Z`);
  for (let i = 0; i < Math.max(1, days); i++) {
    const d = new Date(start.getTime() + i * 86400000);
    wanted.add(d.toISOString().slice(0, 10));
  }

  const byDay = new Map<string, { temps: number[]; precip: number; symbols: string[] }>();
  for (const e of series) {
    const dk = dayKey(e.time);
    if (!wanted.has(dk)) continue;
    const bucket = byDay.get(dk) || { temps: [], precip: 0, symbols: [] };
    const t = e.data?.instant?.details?.air_temperature;
    if (typeof t === 'number') bucket.temps.push(t);
    // Nedbør: bruk 6-timers-blokk (kl 00/06/12/18) for ren døgnsum.
    const hour = Number(e.time.slice(11, 13));
    const six = e.data?.next_6_hours;
    if (six && hour % 6 === 0) {
      bucket.precip += six.details?.precipitation_amount || 0;
      if (six.summary?.symbol_code) bucket.symbols.push(six.summary.symbol_code);
    }
    const one = e.data?.next_1_hours?.summary?.symbol_code;
    if (one) bucket.symbols.push(one);
    byDay.set(dk, bucket);
  }

  const dailies: DailyWeather[] = [];
  for (const dk of Array.from(wanted).sort()) {
    const b = byDay.get(dk);
    if (!b || b.temps.length === 0) continue; // utenfor prognosehorisont
    const precipMm = Math.round(b.precip * 10) / 10;
    // Representativt symbol: foretrekk et nedbør-symbol hvis dagen har det.
    const symbol = b.symbols.find((s) => PRECIP_SYMBOLS.test(s)) || b.symbols[Math.floor(b.symbols.length / 2)] || 'clearsky_day';
    const rainy = precipMm >= RAINY_MM || PRECIP_SYMBOLS.test(symbol);
    dailies.push({
      date: dk,
      symbol,
      precipMm,
      tempMax: Math.round(Math.max(...b.temps)),
      tempMin: Math.round(Math.min(...b.temps)),
      rainy,
    });
  }
  return dailies;
}

/** Verdict + norsk overskrift + snitt-temp fra dags-oppsummering. Ren. */
export function verdictFromDailies(dailies: DailyWeather[]): { verdict: WeatherVerdict; headline: string; rainyDays: number; avgHighC: number } {
  const assessed = dailies.length;
  if (assessed === 0) return { verdict: 'unknown', headline: 'Værmelding ikke tilgjengelig for disse dagene ennå.', rainyDays: 0, avgHighC: 0 };
  const rainyDays = dailies.filter((d) => d.rainy).length;
  const avgHigh = Math.round(dailies.reduce((s, d) => s + d.tempMax, 0) / assessed);
  const ratio = rainyDays / assessed;
  let verdict: WeatherVerdict;
  if (ratio >= 0.5) verdict = 'bad';
  else if (rainyDays === 0) verdict = 'good';
  else verdict = 'mixed';
  const headline =
    verdict === 'bad' ? `Mye nedbør: regn ${rainyDays} av ${assessed} dager (~${avgHigh}°)`
    : verdict === 'good' ? `Stort sett pent vær (~${avgHigh}°)`
    : `Vekslende: regn ${rainyDays} av ${assessed} dager (~${avgHigh}°)`;
  return { verdict, headline, rainyDays, avgHighC: avgHigh };
}

const todayISO = (): string => new Date().toISOString().slice(0, 10);

/**
 * Full værvurdering for et reisemål over reisevinduet. Henter prognose + oppsummerer.
 * startDate default = i dag. `place` er stedsnavn for visning.
 */
export async function assessRouteWeather(
  coords: Coordinates,
  opts: { days?: number; startDate?: string; place?: string },
): Promise<TripWeather> {
  const start = opts.startDate || todayISO();
  const days = opts.days && opts.days > 0 ? opts.days : 3;
  const series = await fetchForecastSeries(coords);
  const dailies = summarizeForecast(series, start, days);
  const { verdict, headline, rainyDays, avgHighC } = verdictFromDailies(dailies);
  return { place: opts.place || 'reisemålet', verdict, headline, assessedDays: dailies.length, rainyDays, avgHighC, dailies, coords };
}

const VERDICT_RANK: Record<WeatherVerdict, number> = { good: 3, mixed: 2, bad: 1, unknown: 0 };

/**
 * Velg det alternativet som har STRENGT bedre vær enn `current` (høyere verdict-rang,
 * eller samme rang men færre regndager). Returnerer null hvis ingen er bedre. Ren.
 */
export function rankBetterWeather(current: TripWeather, candidates: TripWeather[]): TripWeather | null {
  let best: TripWeather | null = null;
  for (const c of candidates) {
    if (c.verdict === 'unknown') continue;
    const betterRank = VERDICT_RANK[c.verdict] > VERDICT_RANK[current.verdict];
    const sameRankDrier = VERDICT_RANK[c.verdict] === VERDICT_RANK[current.verdict] && c.rainyDays < current.rainyDays;
    if (!betterRank && !sameRankDrier) continue;
    if (!best) { best = c; continue; }
    if (VERDICT_RANK[c.verdict] > VERDICT_RANK[best.verdict] ||
        (VERDICT_RANK[c.verdict] === VERDICT_RANK[best.verdict] && c.rainyDays < best.rainyDays)) {
      best = c;
    }
  }
  return best;
}

// ─── Soljakt: Vurdering og batch-uthenting for solrike plasser ─────────────

/**
 * Vurderer om en enkelt dag kvalifiserer som «sol/godvær».
 * - strict: Kun ren sol / skyfritt (clearsky, fair) og minimalt med nedbør (≤0.5 mm).
 * - non-strict: Tillater også pent vær med lette skyer (partlycloudy) og opptil 1.0 mm nedbør.
 */
export function isSunnyDay(daily: DailyWeather, strict = false): boolean {
  if (strict) {
    if (daily.precipMm > 0.5) return false;
    const sym = (daily.symbol || '').toLowerCase();
    return (sym.includes('clearsky') || sym.includes('fair')) && !sym.includes('cloudy') && !sym.includes('rain');
  }
  if (daily.precipMm > 1.5) return false;
  const sym = (daily.symbol || '').toLowerCase();
  return sym.includes('clearsky') || sym.includes('fair') || sym.includes('partlycloudy');
}

/**
 * Vurderer om en dagsrekke (DailyWeather[]) kvalifiserer til sol for gitt periode.
 */
export function isSunnyForecast(
  dailies: DailyWeather[],
  period: SunPeriod = 'today',
  strict = false
): boolean {
  if (!dailies || dailies.length === 0) return false;

  if (period === 'today') {
    return isSunnyDay(dailies[0], strict);
  }

  if (period === 'tomorrow') {
    if (dailies.length < 2) return isSunnyDay(dailies[0], strict);
    return isSunnyDay(dailies[1], strict);
  }

  if (period === '3days') {
    const window = dailies.slice(0, 3);
    if (window.length === 0) return false;
    // Ingen dager bør ha kraftig regnvær (> 6.0 mm i normal modus, > 2.0 mm i strikt)
    const hasHeavyRain = window.some(d => d.precipMm > (strict ? 2.0 : 6.0));
    if (hasHeavyRain) return false;
    // Minst 1 eller 2 dager må ha sol/godvær
    const sunnyCount = window.filter(d => isSunnyDay(d, strict)).length;
    return sunnyCount >= (strict ? 2 : 1);
  }

  if (period === 'weekend') {
    // Finn helgedager (Lørdag / Søndag)
    const weekendDays = dailies.filter(d => {
      const dayOfWeek = new Date(`${d.date}T12:00:00Z`).getUTCDay();
      return dayOfWeek === 6 || dayOfWeek === 0;
    });

    if (weekendDays.length === 0) {
      return isSunnyForecast(dailies, '3days', strict);
    }

    const hasRain = weekendDays.some(d => d.precipMm > (strict ? 2.0 : 6.0));
    if (hasRain) return false;
    return weekendDays.some(d => isSunnyDay(d, strict));
  }

  return isSunnyDay(dailies[0], strict);
}

/**
 * Henter dagsbasert værvarsel for en liste med koordinater i samtidige bolker (concurrency limit).
 * Nærliggende koordinater deler cachet data (avrundet til 2 desimaler).
 */
export async function fetchForecastsForCoordinates(
  coordsList: Coordinates[],
  opts: { days?: number; maxConcurrent?: number } = {}
): Promise<Map<string, DailyWeather[]>> {
  const days = opts.days ?? 4;
  const maxConcurrent = opts.maxConcurrent ?? 6;
  const resultMap = new Map<string, DailyWeather[]>();

  const uniqueKeys = new Map<string, Coordinates>();
  for (const c of coordsList) {
    const k = coordKey(c);
    if (!uniqueKeys.has(k)) {
      uniqueKeys.set(k, c);
    }
  }

  const keysToFetch = Array.from(uniqueKeys.entries());
  const today = todayISO();

  for (let i = 0; i < keysToFetch.length; i += maxConcurrent) {
    const chunk = keysToFetch.slice(i, i + maxConcurrent);
    await Promise.all(
      chunk.map(async ([k, coords]) => {
        try {
          const series = await fetchForecastSeries(coords);
          const dailies = summarizeForecast(series, today, days);
          resultMap.set(k, dailies);
        } catch (e) {
          console.warn(`Forecast fetch failed for ${k}:`, e);
          resultMap.set(k, []);
        }
      })
    );
  }

  return resultMap;
}
